<script setup>
import { deleteRequest, getRequest, putRequest } from "@/utils/request.js";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import SystemSettingFormModal from "@/views/system/setting/modules/SystemSettingFormModal.vue";

const resetPasswordModalRef = ref();
const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const columns = [
  {
    key: "name",
    dataIndex: "name",
    title: "名称",
    width: "15%",
  },
  {
    key: "k",
    dataIndex: "k",
    title: "键",
    width: "15%",
  },
  {
    key: "v",
    dataIndex: "v",
    title: "值",
    width: "15%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "状态",
    width: "15%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/systemSetting", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = () => {
  modalRef.value.title = "系统参数新增";
  modalRef.value.handleAdd();
};

const handleEdit = (record) => {
  modalRef.value.title = "系统参数编辑";
  modalRef.value.handleEdit(record);
};

const handleDelete = (record) => {
  deleteRequest(`/systemSetting/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const handleClose = (record) => {
  putRequest(`/systemSetting/close/${record.id}`).then((response) => {
    if (response.success) {
      message.success("操作成功");
      loadData();
    } else {
      message.error("操作失败。" + response.message);
    }
  });
};

const handleOpen = (record) => {
  putRequest(`/systemSetting/open/${record.id}`).then((response) => {
    if (response.success) {
      message.success("操作成功");
      loadData();
    } else {
      message.error("操作失败。" + response.message);
    }
  });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :label-col="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="名称" name="name">
              <a-input
                v-model:value="searchFormModel.name"
                placeholder="请输入名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="键" name="key">
              <a-input
                v-model:value="searchFormModel.key"
                placeholder="请输入键"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
            >
              重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button
          v-has="'setting:add'"
          type="primary"
          style="margin-right: 18px"
          :icon="h(PlusOutlined)"
          @click="handleAdd"
        >
          新增
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green"> 启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red"> 停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a v-has="'setting:update'" type="link" @click="handleEdit(record)"
              >编辑</a
            >
            <a-divider type="vertical" />
            <a-popconfirm
              v-has="'setting:delete'"
              title="确认要删除吗?"
              @confirm="handleDelete(record)"
            >
              <a v-has="'user:delete'" type="link">删除</a>
            </a-popconfirm>
            <a-divider type="vertical" />
            <a-popconfirm
              v-has="'user:update'"
              v-if="record['status']"
              title="确认要停用吗?"
              @confirm="handleClose(record)"
            >
              <a type="link">停用</a>
            </a-popconfirm>

            <a-popconfirm
              v-has="'user:update'"
              v-else
              title="确认要启用吗?"
              @confirm="handleOpen(record)"
            >
              <a type="link">启用</a>
            </a-popconfirm>
          </template>
        </template>
      </a-table>
    </a-card>
    <system-setting-form-modal ref="modalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
